'use strict'

// Create Agent modal — opened from the "Your agents" home widget
// (root._createAgentOpen). Built on the @symbo.ls/windows chrome through
// the shared Modal wrapper (backdrop, Escape-to-close, stateKey contract,
// URL restore) with a ModalWindow body — the same family as the
// announcement + installable modals.
//
// Fields:
//   Name      — required; slugified server-side into the agent username.
//   Role      — optional free-text caption ("Research assistant").
//   Access    — user-role preset (guest | editor | admin, mirroring the
//               shared SelectRoleField vocabulary); the implied permission
//               set is shown under the select and stored on the agent
//               (User.metadata.userRole + metadata.permissions).
//   Runs in   — project scope: the workspace project the agent operates
//               in, or whole-workspace when unscoped
//               (User.metadata.scopedProject).
//
// Submit routes through el.call('submitCreateAgent', …) → sdk.createAgent.
export const CreateAgentModal = {
  extends: 'Modal',
  stateKey: '_createAgentOpen',
  // Escape/backdrop close also clears the draft — the shared Modal merges
  // extraClose into its close update.
  extraClose: {
    _createAgentName: '',
    _createAgentRole: '',
    _createAgentAccess: 'editor',
    _createAgentProjectId: '',
    _createAgentPending: false,
    _createAgentError: null
  },

  // `Window` matches the shared Modal's slot key so this merges onto its
  // child (which already extends ModalWindow + stops click propagation) —
  // re-declaring the extends here would duplicate the chain (FA806).
  Window: {
    // The centre sheet (DESIGN_SPEC §6.1): the `overlayMd` width token, capped
    // by the veil's inset box — the shared Modal owns the veil, the sheet
    // motion, the phone sheet and the focus/Escape contract.
    width: 'overlayMd',
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    flow: 'y',
    gap: 'A',

    ModalHeader: {
      Hgroup: {
        H: { text: '{{ workspace.createAgent.title | polyglot }}' },
        P: {
          text: '{{ workspace.createAgent.subtitle | polyglot }}'
        }
      }
    },

    Form: {
      tag: 'form',
      novalidate: true,
      flow: 'y',
      gap: 'A',
      onSubmit: async (e, el, s) => {
        e.preventDefault()
        // submitCreateAgent resolves true only when the agent is created
        if (await el.call('submitCreateAgent', el, s.root)) el.call('markPageSaved', el)
      },

      // Every field is the shared field kit (shared/components/AppField.js):
      // AppField owns the caption label and the hint line, AppInput /
      // AppSelect own the control surface; this form keeps the behaviour
      // only. `label` / `hint` are FUNCTION props — the kit also names the
      // control with `label`, and an attribute never resolves a `{{ }}`
      // template.
      NameField: {
        extends: 'AppField',
        label: (el) => el.call('polyglot', 'workspace.createAgent.name'),
        AppInput: {
          placeholder: (el) => el.call('polyglot', 'workspace.createAgent.namePlaceholder'),
          value: (el, s) => s.root?._createAgentName || '',
          onInput: (e, el, s) => s.root.update({ _createAgentName: e.target.value })
        }
      },

      RoleField: {
        extends: 'AppField',
        label: (el) => el.call('polyglot', 'workspace.createAgent.role'),
        AppInput: {
          placeholder: (el) => el.call('polyglot', 'workspace.createAgent.rolePlaceholder'),
          value: (el, s) => s.root?._createAgentRole || '',
          onInput: (e, el, s) => s.root.update({ _createAgentRole: e.target.value })
        }
      },

      AccessField: {
        extends: 'AppField',
        label: (el) => el.call('polyglot', 'workspace.createAgent.access'),
        // The implied permission set, on the kit's hint line under the select.
        hint: (el, s) => el.call('polyglot', el.call('agentAccessHint', s.root)),
        // Native <select> (the kit's AppSelect), CONTROLLED: `value` comes
        // from state, and the framework re-applies a declared select value
        // after the options mount and after every option reconcile (smbls
        // element create.js, syncSelectValue). The `el.node.value` pins this
        // replaced ran only on render/update, so an option reconcile left the
        // select showing another level than the one the create request sends.
        AppSelect: {
          name: 'agent-access',
          value: (el, s) => s.root?._createAgentAccess || 'editor',
          onChange: (e, el, s) => s.root.update({ _createAgentAccess: e.target.value }),
          // A STATIC list — the one AGENT_ACCESS_ROLES vocabulary — and each
          // option resolves its words on its own text effect, so a language
          // or workspace signal never re-emits the options (translate() walks
          // `state.workspace` for a `workspace.*` key).
          childrenAs: 'props',
          children: (el) =>
            el.call('agentAccessOptions').map((o) => ({ value: o.value, labelKey: o.text })),
          childProps: {
            tag: 'option',
            text: (el) => el.call('polyglot', el.labelKey)
          }
        }
      },

      ProjectField: {
        extends: 'AppField',
        label: (el) => el.call('polyglot', 'workspace.createAgent.runsIn'),
        hint: (el, s) =>
          el.call(
            'polyglot',
            s.root?._createAgentProjectId
              ? 'workspace.createAgent.runsInProjectHint'
              : 'workspace.createAgent.runsInWorkspaceHint'
          ),
        // CONTROLLED like the Access select. This list is DATA — the
        // workspace's projects load and change at any time — so it re-emits
        // by design; the declared `value` is what the framework re-applies
        // after every one of those reconciles.
        AppSelect: {
          name: 'agent-project',
          value: (el, s) => s.root?._createAgentProjectId || '',
          onChange: (e, el, s) => s.root.update({ _createAgentProjectId: e.target.value }),
          childrenAs: 'props',
          children: (el, s) =>
            el
              .call('agentProjectOptions', s.root)
              .map((o) => ({ ...o, text: el.call('polyglot', o.text) })),
          childProps: { tag: 'option' }
        }
      },

      ErrorNote: {
        tag: 'p',
        role: 'alert',
        hide: (el, s) => !s.root?._createAgentError,
        margin: '0',
        fontSize: 'Y',
        lineHeight: '1.4',
        // The AA-safe danger pair (the raw brand `red` read under 4.5:1 as
        // text in light).
        color: 'dangerInk',
        text: (el, s) => s.root?._createAgentError || ''
      },

      ModalFooter: {
        flow: 'x',
        gap: 'Z',
        align: 'center',
        justifyContent: 'flex-end',

        // The kit footer (DESIGN_SPEC §6.9, L68): Cancel ghost · primary ink.
        // The kit owns the scale, the washes and the disabled step — never
        // an opacity (Rule 65).
        AppCapsuleBtnGhost: {
          text: '{{ workspace.createAgent.cancel | polyglot }}',
          onClick: (e, el, s) => {
            e.preventDefault()
            el.call('closeCreateAgent', s.root)
          }
        },

        // Create — type:submit so Enter in any field submits the form.
        AppCapsuleBtnPrimary: {
          type: 'submit',
          // The house floor for a primary whose label changes while it works
          // ("Create agent" → "Creating…"), W1's CRM footers alike.
          minWidth: 'E',
          disabled: (el, s) =>
            !(s.root?._createAgentName || '').trim() || !!s.root?._createAgentPending,
          text: (el, s) =>
            el.call(
              'polyglot',
              s.root?._createAgentPending
                ? 'workspace.createAgent.submitting'
                : 'workspace.createAgent.submit'
            )
        }
      }
    }
  }
}
